import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { env } from "@/web/lib/env";
import {
  ShareableCardGate,
  prefetchShareableCard,
} from "@/web/routes/-shareableCardGate/shareableCardGate";
import { CommunityTheaterPage } from "@/web/routes/community-theater/-CommunityTheaterComponents";
import { appUrl } from "@/web/lib/siteUrls";

type CommunityTheaterSearch = {
  pledge?: boolean;
};

function validateCommunityTheaterSearch(raw: Record<string, unknown>): CommunityTheaterSearch {
  return {
    pledge: raw.pledge === true || raw.pledge === "true" ? true : undefined,
  };
}

/** Source filename of the community-theater hero photo on the ImageKit CDN. */
const COMMUNITY_THEATER_HERO_FILENAME = "ct-hero.png";

/**
 * Hero photo cropped to the canonical 1200x630 Open Graph ratio (1.91:1) via
 * an ImageKit path transform. Used as the social-share card so the crop stays
 * predictable even if the underlying hero is ever swapped.
 */
const COMMUNITY_THEATER_HERO_OG_IMAGE = `${env.VITE_WITHCO_IMAGE_CDN_BASE}/tr:w-1200,h-630,c-maintain_ratio,fo-auto/${COMMUNITY_THEATER_HERO_FILENAME}`;

/** Alt text describing the community-theater hero. */
const COMMUNITY_THEATER_HERO_IMAGE_ALT = "Catskill Community Theater marquee on Main Street";

const COMMUNITY_THEATER_PAGE_TITLE = "Community Theater - Catskills | WITH.CO";
const COMMUNITY_THEATER_PAGE_DESCRIPTION = "Community Theater - Catskills";

export const Route = createFileRoute("/community-theater/")({
  ssr: true,
  validateSearch: validateCommunityTheaterSearch,
  loader: async ({ context }) => {
    await prefetchShareableCard(context.queryClient, env.VITE_COMMUNITY_THEATER_CARD_ID);
  },
  head: () => ({
    meta: [
      { title: COMMUNITY_THEATER_PAGE_TITLE },
      { name: "description", content: COMMUNITY_THEATER_PAGE_DESCRIPTION },
      // Open Graph. Explicit dimensions + alt let scrapers render the card
      // without first fetching the image, and keep the crop predictable.
      { property: "og:title", content: COMMUNITY_THEATER_PAGE_TITLE },
      { property: "og:description", content: COMMUNITY_THEATER_PAGE_DESCRIPTION },
      { property: "og:image", content: COMMUNITY_THEATER_HERO_OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: COMMUNITY_THEATER_HERO_IMAGE_ALT },
      { property: "og:url", content: appUrl("/community-theater") },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "WITH.CO" },
      // Twitter Card
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: COMMUNITY_THEATER_PAGE_TITLE },
      { name: "twitter:description", content: COMMUNITY_THEATER_PAGE_DESCRIPTION },
      { name: "twitter:image", content: COMMUNITY_THEATER_HERO_OG_IMAGE },
      { name: "twitter:image:alt", content: COMMUNITY_THEATER_HERO_IMAGE_ALT },
    ],
  }),
  component: CommunityTheaterRoute,
});

function CommunityTheaterRoute() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();

  const handleAutoOpenPledge = useCallback(() => {
    if (!search.pledge) return;

    void navigate({
      search: (prev) => ({
        ...prev,
        pledge: undefined,
      }),
      replace: true,
    });
  }, [navigate, search.pledge]);

  return (
    <ShareableCardGate slug={env.VITE_COMMUNITY_THEATER_CARD_ID}>
      <CommunityTheaterPage
        autoOpenPledge={search.pledge === true}
        onAutoOpenPledgeHandled={handleAutoOpenPledge}
      />
    </ShareableCardGate>
  );
}
